import type { QueryClient } from '@tanstack/react-query';

import type { AuthState } from './machine';

/** What the cache reset needs of the auth client: its live state and a change listener. */
export interface SessionSource {
  readonly state: AuthState;
  subscribe(listener: () => void): () => unknown;
}

function signedInUserId(state: AuthState): string | null {
  return state.status === 'authenticated' ? state.session.user.userId : null;
}

/**
 * Drop every cached answer when a **different** user signs in (HS-1659 in admin, HS-1579 in
 * portal).
 *
 * Signing out only navigates to `/login`: the tab, and the query cache with it, lives on. Without
 * this, the next person to sign in on that tab is first shown the previous person's lists, figures
 * and names — every cached query renders before its refetch lands. The listener runs synchronously
 * as the session turns `authenticated`, before the app navigates past the login page, so nothing
 * of the previous user is ever drawn.
 *
 * The cache is cleared on the next user's sign-in rather than on sign-out on purpose: at sign-out
 * the previous user's pages are still mounted, and clearing under them makes their queries fetch
 * again without a session. A token refresh, or the same user signing back in, keeps the cache.
 *
 * A user can also change under mounted pages: another tab signs in as someone else on the shared
 * cookie, and this tab's session re-read reports the new user. `clear()` would leave those pages
 * rendering their detached query's old data, so the queries on screen are **reset** instead — back
 * to loading, then refetched for the new user — and only the rest are dropped.
 *
 * The query cache is not the only place a user's data can outlive them. `onUserChange` runs on the
 * same signal, right after the cache is dropped, for whatever else the app keeps per user — portal
 * clears its router's cached matches there, whose loader data carries the previous user's id.
 *
 * Call it once per app, before the first sign-in. Returns the unsubscribe.
 */
export function clearCacheOnUserChange(
  session: SessionSource,
  cache: QueryClient,
  onUserChange?: () => void,
): () => unknown {
  let owner = signedInUserId(session.state);
  return session.subscribe(() => {
    const userId = signedInUserId(session.state);
    if (userId === null || userId === owner) return;
    if (owner !== null) {
      cache.removeQueries({ type: 'inactive' });
      void cache.resetQueries({ type: 'active' });
      onUserChange?.();
    }
    owner = userId;
  });
}
